import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { backupAPI } from '../services/backupApi';
import { Button, Input } from '../components';
import { Download, LogOut, Mail, Lock, HardDrive } from 'lucide-react';

interface BackupItem {
  name: string;
  size: number;
  sizeMB: string;
  date: string;
  type: string;
}

export const BackupPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [listLoading] = useState(false);

  const checkAuth = async () => {
    try {
      const res = await backupAPI.getList();
      if (res.data?.success) {
        setAuthenticated(true);
        setBackups(res.data.data || []);
      }
    } catch {
      setAuthenticated(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await backupAPI.login(email, password);
      if (res.data?.success) {
        setAuthenticated(true);
        const listRes = await backupAPI.getList();
        setBackups(listRes.data?.data || []);
      } else {
        setError(t('backup.invalidCredentials'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('backup.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await backupAPI.logout();
    } catch {
      /* ignore */
    }
    setAuthenticated(false);
    setBackups([]);
  };

  const handleDownload = async (filename: string) => {
    const base = import.meta.env.VITE_API_URL
      ? String(import.meta.env.VITE_API_URL).replace(/\/api\/?$/, '')
      : window.location.origin;
    const url = `${base}/api/backup/download/${encodeURIComponent(filename)}`;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(t('backup.downloadFailed'));
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(url, '_blank');
    }
  };

  const typeLabel: Record<string, string> = {
    db: t('backup.typeDb'),
    app: t('backup.typeApp'),
    weekly: t('backup.typeWeekly'),
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0B1F3A]/10 mb-4">
              <HardDrive className="w-8 h-8 text-[#0B1F3A]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t('backup.title')}</h1>
            <p className="mt-2 text-sm text-gray-600">{t('backup.subtitle')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                label={t('backup.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('backup.emailPlaceholder')}
                icon={<Mail className="w-5 h-5" />}
                required
              />
              <Input
                label={t('backup.passwordLabel')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<Lock className="w-5 h-5" />}
                required
              />
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              <Button type="submit" loading={loading} className="w-full">
                {t('backup.loginButton')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#0B1F3A]/10">
              <HardDrive className="w-6 h-6 text-[#0B1F3A]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('backup.backupsTitle')}</h1>
              <p className="text-sm text-gray-600">{t('backup.backupsSubtitle')}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            {t('backup.logout')}
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {listLoading ? (
            <div className="p-8 text-center text-gray-500">{t('backup.loading')}</div>
          ) : backups.length === 0 ? (
            <div className="p-8 text-center text-gray-500">{t('backup.noBackups')}</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {backups.map((b) => (
                <li key={b.name} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{b.name}</p>
                    <p className="text-sm text-gray-500">
                      {typeLabel[b.type] || b.type} · {b.sizeMB} MB ·{' '}
                      {new Date(b.date).toLocaleDateString('nl-NL')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(b.name)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('backup.download')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
